import React, { useState, useEffect } from "react";
import {
  X,
  Lock,
  Mail,
  User as UserIcon,
  Sparkles,
  Check,
  AlertCircle,
  Loader2,
  ArrowRight,
  ShieldCheck,
  Send,
  RotateCw,
} from "lucide-react";
import { useAuth } from "../context/AuthContext.js";
import { supabase } from "../lib/supabase.js";

// Custom GitHub Octocat SVG
export const GithubIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
    />
  </svg>
);

// Custom Google G-Logo SVG
export const GoogleIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.98 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
    />
  </svg>
);

export interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { signInWithPassword, signUp, signInWithOAuth, isAuthModalOpen, setIsAuthModalOpen } =
    useAuth();

  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  // Resend state
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Reset state on open/close or Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      setError(null);
      setIsSuccess(false);
      setResendStatus(null);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleClose = () => {
    setShowEmailConfirmation(false);
    setError(null);
    setIsSuccess(false);
    setResendStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Please enter both email and password.");
      return;
    }

    if (mode === "signup" && password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (mode === "signin") {
        const res = await signInWithPassword(email, password);
        if (res.error) {
          setError(res.error.message || "Invalid email or password.");
          setIsSubmitting(false);
          return;
        }

        setIsSuccess(true);
        setIsSubmitting(false);
        setTimeout(() => {
          handleClose();
        }, 600);
      } else {
        const res = await signUp(email, password, displayName);
        if (res.error) {
          setError(res.error.message || "Failed to create account.");
          setShowEmailConfirmation(false);
          setIsSubmitting(false);
          return;
        }

        setIsSubmitting(false);

        // Check if session was returned immediately (email confirmation disabled)
        if (res.session) {
          setIsSuccess(true);
          setTimeout(() => {
            handleClose();
          }, 600);
        } else {
          // Email confirmation required: show Check Your Inbox screen
          setSubmittedEmail(email.trim());
          setShowEmailConfirmation(true);
          setResendStatus(null);
        }
      }
    } catch (err: any) {
      setError(err?.message || "An unexpected error occurred.");
      setShowEmailConfirmation(false);
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!submittedEmail) return;
    setIsResending(true);
    setResendStatus(null);

    try {
      const { error: resendErr } = await supabase.auth.resend({
        type: "signup",
        email: submittedEmail,
      });

      if (resendErr) {
        setResendStatus({
          type: "error",
          message:
            resendErr.message ||
            "Failed to resend verification email. Please try again in a few minutes.",
        });
      } else {
        setResendStatus({
          type: "success",
          message: "Verification email resent successfully!",
        });
      }
    } catch (err: any) {
      setResendStatus({
        type: "error",
        message: err?.message || "An unexpected error occurred while resending.",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleOAuth = async (provider: "github" | "google") => {
    setError(null);
    try {
      await signInWithOAuth(provider);
    } catch (err: any) {
      setError(err?.message || `Failed to sign in with ${provider}.`);
    }
  };

  return (
    <div
      onClick={handleClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200 font-sans"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-3xl shadow-2xl p-6 sm:p-8 space-y-5 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Top Accent Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-2 bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 rounded-full blur-sm" />

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
          title="Close Modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* 1. CHECK YOUR INBOX SCREEN (When email confirmation is required) */}
        {showEmailConfirmation ? (
          <div className="space-y-5 py-2 text-center animate-in fade-in zoom-in-95 duration-200">
            {/* Emerald Mail Illustration */}
            <div className="w-16 h-16 rounded-3xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mx-auto shadow-inner shadow-emerald-500/20">
              <Mail className="w-8 h-8 text-emerald-400 animate-pulse" />
            </div>

            <div className="space-y-2">
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                Check Your Inbox
              </h2>
              <p className="text-xs text-zinc-300 max-w-sm mx-auto leading-relaxed">
                We&apos;ve sent a verification link to{" "}
                <span className="text-emerald-400 font-semibold">{submittedEmail}</span>. Click the link in the email to activate your account and start streaming.
              </p>
            </div>

            {/* Inline Resend Alert */}
            {resendStatus && (
              <div
                className={`rounded-2xl px-3.5 py-2.5 text-xs flex items-center gap-2.5 animate-in fade-in duration-150 ${
                  resendStatus.type === "success"
                    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
                    : "bg-red-500/10 border border-red-500/30 text-red-400"
                }`}
              >
                {resendStatus.type === "success" ? (
                  <Check className="w-4 h-4 shrink-0 text-emerald-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
                )}
                <span className="leading-tight">{resendStatus.message}</span>
              </div>
            )}

            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 text-xs text-zinc-400 text-left space-y-2.5">
              <div className="flex items-center gap-2 text-zinc-200 font-bold">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>Next steps to complete registration:</span>
              </div>
              <ol className="list-decimal list-inside space-y-1 text-[11px] text-zinc-400 pl-1 leading-relaxed">
                <li>Check your inbox (and spam/promotions folder).</li>
                <li>Click the confirmation link to activate your profile.</li>
                <li>Return to HP - Helix to begin streaming and uploading!</li>
              </ol>
            </div>

            <div className="space-y-3 pt-1">
              <button
                type="button"
                onClick={handleClose}
                className="w-full py-3 px-4 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-xs transition-all duration-150 transform active:scale-95 shadow-lg shadow-emerald-500/20"
              >
                Done / Close
              </button>

              {/* Resend Verification Button */}
              <div className="text-center pt-1">
                <button
                  type="button"
                  disabled={isResending}
                  onClick={handleResendVerification}
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-emerald-400 font-medium transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                      <span>Resending verification link...</span>
                    </>
                  ) : (
                    <span>Didn&apos;t receive the email? Resend verification link</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* 2. SIGN IN / CREATE ACCOUNT FORM */
          <>
            {/* Header Branding */}
            <div className="text-center space-y-1 pt-1">
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                {mode === "signin" ? "Sign In to HP - Helix" : "Create your Helix Account"}
              </h2>
              <p className="text-xs text-zinc-400">
                {mode === "signin"
                  ? "Access your creator channel, uploads, and personalized feed."
                  : "Join HP - Helix to stream, upload, and support independent creators."}
              </p>
            </div>

            {/* Mode Selector Tabs */}
            <div className="flex bg-zinc-900/90 p-1 rounded-2xl border border-zinc-800/80">
              <button
                type="button"
                onClick={() => {
                  setMode("signin");
                  setError(null);
                }}
                className={`flex-1 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  mode === "signin"
                    ? "bg-zinc-800 text-white shadow-sm"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("signup");
                  setError(null);
                }}
                className={`flex-1 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  mode === "signup"
                    ? "bg-zinc-800 text-white shadow-sm"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                Create Account
              </button>
            </div>

            {/* Error Alert Display */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-2xl px-3.5 py-2.5 text-xs flex items-center gap-2.5 animate-in fade-in duration-150">
                <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
                <span className="leading-tight">{error}</span>
              </div>
            )}

            {/* Success Alert Display */}
            {isSuccess && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-2xl px-3.5 py-2.5 text-xs flex items-center gap-2.5 animate-in fade-in duration-150">
                <Check className="w-4 h-4 shrink-0 text-emerald-400" />
                <span>Authenticated successfully! Redirecting...</span>
              </div>
            )}

            {/* Email & Password Form */}
            <form onSubmit={handleSubmit} className="space-y-3.5">
              {mode === "signup" && (
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-zinc-300">Display Name</label>
                  <div className="relative flex items-center">
                    <UserIcon className="w-4 h-4 text-zinc-500 absolute left-3.5 pointer-events-none" />
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="e.g. Afrobeats King"
                      className="w-full bg-zinc-900/90 border border-zinc-800 rounded-2xl pl-10 pr-3.5 py-2.5 text-xs text-white placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500/80 focus:ring-2 focus:ring-emerald-500/20 transition-all font-sans"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-zinc-300">Email Address</label>
                <div className="relative flex items-center">
                  <Mail className="w-4 h-4 text-zinc-500 absolute left-3.5 pointer-events-none" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-zinc-900/90 border border-zinc-800 rounded-2xl pl-10 pr-3.5 py-2.5 text-xs text-white placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500/80 focus:ring-2 focus:ring-emerald-500/20 transition-all font-sans"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-zinc-300">Password</label>
                <div className="relative flex items-center">
                  <Lock className="w-4 h-4 text-zinc-500 absolute left-3.5 pointer-events-none" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-zinc-900/90 border border-zinc-800 rounded-2xl pl-10 pr-3.5 py-2.5 text-xs text-white placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500/80 focus:ring-2 focus:ring-emerald-500/20 transition-all font-sans"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isSuccess}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-xs transition-all duration-150 transform active:scale-95 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>{mode === "signin" ? "Sign In with Email" : "Create Helix Account"}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Or Divider */}
            <div className="relative flex items-center justify-center">
              <div className="border-t border-zinc-800 w-full" />
              <span className="bg-zinc-950 px-3 text-[10px] font-bold uppercase text-zinc-500 tracking-wider">
                Or continue with
              </span>
              <div className="border-t border-zinc-800 w-full" />
            </div>

            {/* OAuth Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleOAuth("github")}
                className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-2xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white font-bold text-xs transition-all active:scale-95"
              >
                <GithubIcon className="w-4 h-4 text-white" />
                <span>GitHub</span>
              </button>

              <button
                type="button"
                onClick={() => handleOAuth("google")}
                className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-2xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white font-bold text-xs transition-all active:scale-95"
              >
                <GoogleIcon className="w-4 h-4" />
                <span>Google</span>
              </button>
            </div>

            <p className="text-[10px] text-zinc-500 text-center leading-tight">
              By signing in, you agree to the HP - Helix terms of service and creator community guidelines.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
